import React, { useState, useEffect } from 'react'
import { useParams, useOutletContext, useNavigate } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { 
  Calendar, MapPin, Clock, Users, CheckCircle, XCircle, HelpCircle,
  ArrowLeft, Edit2, Trash2, Shirt, FileText
} from 'lucide-react'

function formatDateTime(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { 
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
  }) + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

const rsvpOptions = [
  { value: 'going', label: 'Going', icon: CheckCircle, color: 'bg-green-500/10 text-green-400 border-green-500/30' },
  { value: 'maybe', label: 'Maybe', icon: HelpCircle, color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  { value: 'not_going', label: "Can't Go", icon: XCircle, color: 'bg-red-500/10 text-red-400 border-red-500/30' },
]

const eventTypeLabels = {
  practice: 'Practice',
  game: 'Game',
  tournament: 'Tournament',
  meeting: 'Meeting',
  social: 'Social Event',
}

export default function PortalEventDetail() {
  const { eventId } = useParams()
  const { team, canManage, token } = useOutletContext()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [rsvps, setRsvps] = useState([])
  const [myRsvp, setMyRsvp] = useState(null)
  const [loading, setLoading] = useState(true)
  const [rsvpLoading, setRsvpLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (eventId) {
      fetchEvent()
      fetchRsvps()
    }
  }, [eventId])

  const fetchEvent = async () => {
    try {
      const data = await portalRequest(`/api/events/${eventId}`)
      setEvent(data)
    } catch (err) {
      console.error('Error fetching event:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchRsvps = async () => {
    try {
      const data = await portalRequest(`/api/events/${eventId}/rsvps`)
      const list = Array.isArray(data) ? data : data?.rsvps || []
      setRsvps(list)
      // Find current user's RSVP
      const token = localStorage.getItem('portal_token')
      if (token) {
        try {
          const me = await portalRequest('/api/auth/me')
          const mine = list.find(r => r.user_id === me._id || r.user_id === me.user_id)
          if (mine) setMyRsvp(mine.status)
        } catch {}
      }
    } catch (err) {
      console.error('Error fetching RSVPs:', err)
    }
  }

  const handleRsvp = async (status) => {
    setRsvpLoading(true)
    try {
      await portalRequest(`/api/events/${eventId}/rsvp`, {
        method: 'POST',
        body: JSON.stringify({ status })
      })
      setMyRsvp(status)
      fetchRsvps()
    } catch (err) {
      console.error('RSVP error:', err)
    } finally {
      setRsvpLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return
    setDeleting(true)
    try {
      await portalRequest(`/api/events/${eventId}`, { method: 'DELETE' })
      navigate(`/portal/team/${team.team_id}/schedule`)
    } catch (err) {
      console.error('Delete error:', err)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500"></div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="text-center py-20">
        <XCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
        <p className="text-zinc-400">Event not found</p>
      </div>
    )
  }

  const rsvpCounts = {
    going: rsvps.filter(r => r.status === 'going').length,
    maybe: rsvps.filter(r => r.status === 'maybe').length,
    not_going: rsvps.filter(r => r.status === 'not_going' || r.status === 'no').length,
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back */}
      <button
        onClick={() => navigate(`/portal/team/${team.team_id}/schedule`)}
        className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm font-medium">Back to Schedule</span>
      </button>

      {/* Event Header */}
      <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="inline-block px-3 py-1 rounded-lg text-xs font-medium bg-zinc-700 text-zinc-300 mb-3">
              {eventTypeLabels[event.event_type] || event.event_type}
            </span>
            <h1 className="text-2xl font-bold text-white">{event.title}</h1>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/portal/team/${team.team_id}/events/${eventId}/edit`)}
                className="p-2 rounded-lg hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3 text-zinc-300">
            <Calendar className="w-5 h-5 text-zinc-500 flex-shrink-0" />
            <span>{formatDateTime(event.event_date)}</span>
          </div>
          {event.end_date && (
            <div className="flex items-center gap-3 text-zinc-300">
              <Clock className="w-5 h-5 text-zinc-500 flex-shrink-0" />
              <span>Until {formatDateTime(event.end_date)}</span>
            </div>
          )}
          {event.arrival_time && (
            <div className="flex items-center gap-3 text-zinc-300">
              <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <span>Arrive by {event.arrival_time}</span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-3 text-zinc-300">
              <MapPin className="w-5 h-5 text-zinc-500 flex-shrink-0" />
              <span>{event.location}</span>
            </div>
          )}
          {event.uniform_color && (
            <div className="flex items-center gap-3 text-zinc-300">
              <Shirt className="w-5 h-5 text-zinc-500 flex-shrink-0" />
              <span>Wear: {event.uniform_color}</span>
            </div>
          )}
        </div>

        {(event.description || event.notes) && (
          <div className="mt-4 pt-4 border-t border-zinc-700">
            {event.description && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
                <p className="text-zinc-300 whitespace-pre-wrap">{event.description}</p>
              </div>
            )}
            {event.notes && (
              <div className="flex items-start gap-3 mt-2">
                <FileText className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
                <p className="text-zinc-400 text-sm whitespace-pre-wrap">{event.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Map preview */}
        {event.location_coords && (
          <div className="mt-4 rounded-xl overflow-hidden border border-zinc-700 h-48">
            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${event.location_coords.lat},${event.location_coords.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full h-full bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-zinc-200 transition-colors"
            >
              <div className="text-center">
                <MapPin className="w-8 h-8 mx-auto mb-2" />
                <span className="text-sm font-medium">Open in Maps</span>
              </div>
            </a>
          </div>
        )}
      </div>

      {/* RSVP */}
      <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Your RSVP</h2>
        <div className="flex flex-wrap gap-3">
          {rsvpOptions.map(opt => {
            const Icon = opt.icon
            const isSelected = myRsvp === opt.value
            return (
              <button
                key={opt.value}
                onClick={() => handleRsvp(opt.value)}
                disabled={rsvpLoading}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium border transition-all ${
                  isSelected
                    ? opt.color + ' ring-2 ring-offset-2 ring-offset-zinc-800'
                    : 'border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600'
                } ${isSelected ? 'ring-green-500/30' : ''}`}
              >
                <Icon className="w-5 h-5" />
                {opt.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* RSVP Breakdown */}
      <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-zinc-400" />
          Responses ({rsvps.length})
        </h2>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 rounded-xl bg-green-500/5 border border-green-500/10">
            <p className="text-2xl font-bold text-green-400">{rsvpCounts.going}</p>
            <p className="text-xs text-zinc-400 mt-1">Going</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
            <p className="text-2xl font-bold text-amber-400">{rsvpCounts.maybe}</p>
            <p className="text-xs text-zinc-400 mt-1">Maybe</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-red-500/5 border border-red-500/10">
            <p className="text-2xl font-bold text-red-400">{rsvpCounts.not_going}</p>
            <p className="text-xs text-zinc-400 mt-1">Can't Go</p>
          </div>
        </div>

        {rsvps.length > 0 && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {rsvps.map((rsvp, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-zinc-750">
                <span className="text-sm text-zinc-200">{rsvp.user_name || rsvp.player_name || 'Member'}</span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                  rsvp.status === 'going' ? 'text-green-400 bg-green-500/10' :
                  rsvp.status === 'maybe' ? 'text-amber-400 bg-amber-500/10' :
                  'text-red-400 bg-red-500/10'
                }`}>
                  {rsvp.status === 'not_going' || rsvp.status === 'no' ? "Can't Go" : 
                   rsvp.status.charAt(0).toUpperCase() + rsvp.status.slice(1)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
